import { faMicrophone, faRecordVinyl } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Grid, Typography, useTheme } from "@mui/material";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RecordingHint, StyledDialogContent } from "./styles";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
interface Props {
  handleAudio: (audioURL: string, audioBlob: Blob) => void;
  onlyIcon?: boolean;
  customButtonStyles?: React.CSSProperties;
  filter?: boolean;
  customSaveName?: string;
}

interface State {
  open: boolean;
  isRecording: boolean;
  audioURL: string;
  audioBlob: Blob | null;
  filteredAudioBlob: Blob | null;
  filteredAudioURL: string;
  time: number;
  CCDOpen: boolean;
}

const VoiceRecorder = ({
  handleAudio,
  onlyIcon = false,
  customButtonStyles,
  filter = false,
  customSaveName,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.voice_recorder";
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      isRecording: false,
      audioURL: "",
      audioBlob: null,
      filteredAudioBlob: null,
      filteredAudioURL: "",
      time: 0,
      CCDOpen: false,
    }),
    []
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
        const audioURL = URL.createObjectURL(audioBlob);
        setState((state) => ({ ...state, audioURL, audioBlob }));
        // filter && applyFilter();
        audioChunks.current = [];
      };

      mediaRecorderRef.current.start();
      setState((state) => ({ ...state, isRecording: true }));
      intervalRef.current = window.setInterval(() => {
        setState((state) => ({ ...state, time: state.time + 10 }));
      }, 10);
    } catch (err) {
      console.error("Error accessing microphone: ", err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setState((state) => ({
      ...state,
      isRecording: false,
      time: 0,
    }));
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleConfirm = useCallback(() => {
    if (state.audioURL && state.audioBlob) {
      handleAudio(state.audioURL, state.audioBlob);
      setState((state) => ({ ...state, open: false, audioBlob: null, audioURL: "" }));
    }
  }, [state.audioURL, state.audioBlob, handleAudio]);

  const handleDiscard = useCallback(() => {
    setState({ ...INITIAL_STATE });
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, [INITIAL_STATE]);

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setState((state) => ({ ...state, open: true }))}
        {...(customButtonStyles && {
          style: { ...customButtonStyles },
        })}
      >
        <FontAwesomeIcon icon={faMicrophone} /> &nbsp;
        {!onlyIcon && `${t(`${tRP}.record_voice_message`)}`}
      </Button>
      <Dialog
        open={state.open}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.record_voice_message`)}`}
          handleClose={() => {
            setState((state) => ({ ...state, CCDOpen: true }));
          }}
          {...(state.isRecording && {
            headerActionButtons: (
              <RecordingHint
                icon={faRecordVinyl}
                color={theme.palette.error.main}
              />
            ),
          })}
        />
        <StyledDialogContent>
          {state.audioURL ? (
            <Grid>
              <audio
                controls
                src={state.audioURL}
              >
                <track
                  kind="captions"
                  srcLang="en"
                  label="English captions"
                />
              </audio>
            </Grid>
          ) : (
            <>
              <Typography variant="h3">
                {`
                      ${
                        Math.floor(state.time / 3600000) > 9
                          ? Math.floor(state.time / 3600000)
                          : `0${Math.floor(state.time / 3600000)}`
                      } : 
                      ${
                        Math.floor((state.time % 3600000) / 60000) > 9
                          ? Math.floor((state.time % 3600000) / 60000)
                          : `0${Math.floor((state.time % 3600000) / 60000)}`
                      } : 
                      ${
                        Math.floor((state.time % 60000) / 1000) > 9
                          ? Math.floor((state.time % 60000) / 1000)
                          : `0${Math.floor((state.time % 60000) / 1000)}`
                      }
                    `}
              </Typography>
              {state.isRecording ? (
                <Button
                  variant="contained"
                  style={{ backgroundColor: theme.palette.error.main }}
                  onClick={stopRecording}
                >
                  {" "}
                  {`${t(`${tRP}.stop_recording`)}`}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  style={{ backgroundColor: theme.palette.success.main }}
                  onClick={startRecording}
                >
                  {" "}
                  {`${t(`${tRP}.start_recording`)}`}
                </Button>
              )}
            </>
          )}
        </StyledDialogContent>
        <DialogActions
          handleCancel={() => {
            setState((state) => ({ ...state, CCDOpen: true }));
          }}
          {...(state.audioURL &&
            state.audioBlob && {
              handleSave: handleConfirm,
              customSaveName: customSaveName ?? `${t(`${tRP}.confirm`)}`,
            })}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleDiscard={handleDiscard}
        handleCancel={() => {
          setState((state) => ({ ...state, CCDOpen: false }));
        }}
      />
    </>
  );
};

export default VoiceRecorder;
